import React, { useRef, useState } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Switch, Table } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import "./data_goods_receive.css";
import useSWR from "swr";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getItemGroupLabel } from "../../components/hoc_goods_receive";

const DataGoodsReceive: React.FC = () => {
     const transformStyle = { transform: "scale(0.80)", transformOrigin: "top left", width: "125%" };

     interface DataType {
          key: string;
          goods_receive_no: string;
          item_group: string;
          date: string;
          ref_doc_no: string;
          supplier_name: string;
          total_receive: number;
          total_amount: number;
          warehouse: string;
          status: string;
     }

     type DataIndex = keyof DataType;
     const [searchText, setSearchText] = useState("");
     const [searchedColumn, setSearchedColumn] = useState("");
     const [tableParams, setTableParams] = useState<{ pagination: { current: number; pageSize: number }; filters: any; sortField: string; sortOrder: string }>({ pagination: { current: 1, pageSize: 10 }, filters: {}, sortField: '', sortOrder: '' });
     const searchInput = useRef<InputRef>(null);
     const navigate = useNavigate();
     const { userData } = useAuth();
     const [showAllWarehouse, setShowAllWarehouse] = useState(false);
     const hasAuthority77 = userData?.otoritas ? userData.otoritas.split(";").map((val: string) => val.trim()).includes("77") : false;
     const fetcher = (url: string) => axios.get(url).then((res) => res.data);
     const warehouseQuery = !showAllWarehouse && userData?.warehouse_code ? `&warehouse=${encodeURIComponent(userData.warehouse_code)}` : "";
     const { data, error, isLoading } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/goods-receive-all?page=${tableParams.pagination.current}&pageSize=${tableParams.pagination.pageSize}&sortField=${tableParams.sortField}&sortOrder=${tableParams.sortOrder}&filters=${JSON.stringify(tableParams.filters)}${warehouseQuery}`, fetcher);

     if (error) {
          console.error("Error fetching Goods Receive data:", error);
     }

     const handleSearch = (
          selectedKeys: string[],
          confirm: FilterDropdownProps["confirm"],
          dataIndex: DataIndex,
     ) => {
          confirm();
          setSearchText(selectedKeys[0]);
          setSearchedColumn(dataIndex);
     };

     const handleReset = (clearFilters: () => void) => {
          clearFilters();
          setSearchText("");
     };

     const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
               <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                         ref={searchInput}
                         placeholder={`Search ${String(dataIndex)
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (char) => char.toUpperCase())}`}
                         value={selectedKeys[0]}
                         onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                         onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                         style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                         <Button
                              type="primary"
                              onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                              icon={<SearchOutlined />}
                              size="small"
                              style={{ width: 90 }}>
                              Search
                         </Button>
                         <Button
                              onClick={() => clearFilters && handleReset(clearFilters)}
                              size="small"
                              style={{ width: 90 }}>
                              Reset
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   confirm({ closeDropdown: false });
                                   setSearchText((selectedKeys as string[])[0]);
                                   setSearchedColumn(dataIndex);
                              }}>
                              Filter
                         </Button>
                         <Button
                              type="link"
                              size="small"
                              onClick={() => {
                                   close();
                              }}>
                              close
                         </Button>
                    </Space>
               </div>
          ),
          filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
          onFilter: (value, record) => {
               const recordValue = record[dataIndex];
               return recordValue
                    ? recordValue.toString().toLowerCase().includes((value as string).toLowerCase())
                    : false;
          },
          filterDropdownProps: {
               onOpenChange(open) {
                    if (open) {
                         setTimeout(() => searchInput.current?.select(), 100);
                    }
               },
          },
          render: (text) =>
               searchedColumn === dataIndex ? (
                    <Highlighter
                         highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                         searchWords={[searchText]}
                         autoEscape
                         textToHighlight={text ? text.toString() : ""}
                    />
               ) : (
                    text
               ),
     });

     const warehouseFilters = (data?.warehouses || []).map((w: { code: string; name: string }) => ({
          text: w.name || w.code,
          value: w.code,
     }));

     const columns: TableColumnsType<DataType> = [
          {
               title: "Goods Receive No.",
               dataIndex: "goods_receive_no",
               key: "goods_receive_no",
               width: "15%",
               ...getColumnSearchProps("goods_receive_no"),
               sorter: true,
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Item Group",
               dataIndex: "item_group",
               key: "item_group",
               width: "10%",
               ...getColumnSearchProps("item_group"),
               sorter: (a, b) => (a.item_group || "").localeCompare(b.item_group || ""),
               sortDirections: ["ascend", "descend"],
               render: (text: string) => {
                    const label = getItemGroupLabel(text);
                    return searchedColumn === "item_group" ? (
                         <Highlighter
                              highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                              searchWords={[searchText]}
                              autoEscape
                              textToHighlight={label ? label.toString() : ""}
                         />
                    ) : (
                         label
                    );
               },
          },
          {
               title: "Date",
               dataIndex: "date",
               key: "date",
               width: "10%",
               ...getColumnSearchProps("date"),
               sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Ref Doc No.",
               dataIndex: "ref_doc_no",
               key: "ref_doc_no",
               width: "15%",
               ...getColumnSearchProps("ref_doc_no"),
               sorter: (a, b) => a.ref_doc_no.localeCompare(b.ref_doc_no),
               sortDirections: ["ascend", "descend"],
          },
          {
               title: "Supplier Name",
               dataIndex: "supplier_name",
               key: "supplier_name",
               width: "20%",
               ...getColumnSearchProps("supplier_name"),
               sorter: (a, b) => a.supplier_name.localeCompare(b.supplier_name),
               sortDirections: ["ascend", "descend"],
          },

          {
               title: "Received Qty",
               dataIndex: "total_receive",
               key: "total_receive",
               width: "10%",
               ...getColumnSearchProps("total_receive"),
               sorter: (a, b) => a.total_receive - b.total_receive,
               sortDirections: ["ascend", "descend"],
          },

          {
               title: "Total Amount",
               dataIndex: "total_amount",
               key: "total_amount",
               width: "10%",
               ...getColumnSearchProps("total_amount"),
               sorter: (a, b) => a.total_amount - b.total_amount,
               sortDirections: ["ascend", "descend"],
               render: (text: number | string) => Number(text || 0).toLocaleString("id-ID"),
          },

          {
               title: "Warehouse",
               dataIndex: "warehouse",
               key: "warehouse",
               width: "10%",
               filters: warehouseFilters,
               onFilter: (value, record) => record.warehouse === value,
               filterMultiple: true,
               onCell: () => ({ style: { whiteSpace: "nowrap" } }),
          },

          {
               title: "Order Status",
               dataIndex: "status",
               key: "status",
               width: "10%",
               ...getColumnSearchProps("status"),
               sorter: (a, b) => a.status.localeCompare(b.status),
               sortDirections: ["ascend", "descend"],
               render: (text: string) => {
                    if (text === '1') {
                         return 'Draft';
                    }
                    if (text === '4') {
                         return 'Complete';
                    }
                    return text;
               },
          },
     ];

     return (
          <div id="ieml" style={transformStyle}>
               <div className="gjs-grid-row" id="iwyh">
                    <div className="gjs-grid-column" id="iqcg">
                         <Breadcrumb
                              items={[
                                   {
                                        title: (
                                             <>
                                                  <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Goods Receive</span>
                                             </>
                                        ),
                                   },
                                   { href: "#/dashboard/goods_receive", title: "Goods Receive" },
                              ]}
                         />
                    </div>
               </div>

               <div className="gjs-grid-row" id="iy8o">
                    <div className="gjs-grid-column" id="ibu3">
                         {hasAuthority77 && (
                              <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                   <Switch checked={showAllWarehouse} onChange={(checked) => { setShowAllWarehouse(checked); setTableParams((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } })); }} />
                                   <span style={{ fontSize: "14px" }}>Show All Warehouse</span>
                              </div>
                         )}
                         <Table<DataType>
                              columns={columns}
                              dataSource={data?.data || []}
                              rowKey="goods_receive_no"
                              scroll={{ x: "max-content" }}
                              pagination={{ ...tableParams.pagination, total: data?.total }}
                              loading={isLoading}
                              onChange={(pagination, filters, sorter: any) => setTableParams({ pagination: { current: pagination.current ?? 1, pageSize: pagination.pageSize ?? 10 }, filters, sortField: sorter.field ?? '', sortOrder: sorter.order ?? '' })}

                              onRow={(record) => {
                                   return {
                                        onDoubleClick: () => {
                                             navigate("/dashboard/goods_receive", { state: { goodsReceiveNo: record.goods_receive_no } });
                                        },
                                   };
                              }}
                         />
                    </div>
               </div>
          </div>
     );
};

export default DataGoodsReceive;
